/**
 * Specialty adoption dashboard — `/governance/specialty-adoption`.
 *
 * rev v11.0.0 · Phase 2 / C6
 *
 * The dashboard that makes a staged rollout observable. It answers three
 * questions:
 *
 *   • which specialties are enabled for this tenant?
 *   • how far along is the rollout?
 *   • what is the catalogue state of each specialty?
 *
 * It reads the flag service; it never writes it. Changing the rollout is an
 * operations decision (an environment variable), not a button in the
 * interface — a button would put a production toggle in the hands of every
 * superadmin.
 */
import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { Activity, ArrowRight, CheckCircle2, CircleSlash, TriangleAlert } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSpecialtyRollout } from "@/hooks/use-specialty-flag";
import { ALL_SPECIALTIES, getSpecialty } from "@/lib/specialty";
import { NGAP_CATALOG_HASH } from "@/lib/ngap-catalogue";
import { ROUTES, asRoutePath } from "@/lib/routes";

export const Route = createFileRoute(asRoutePath(ROUTES.specialtyAdoption))({
  component: SpecialtyAdoption,
});

function SpecialtyAdoption() {
  const rollout = useSpecialtyRollout();

  const bySlug = new Map(rollout.flags.map((f) => [f.specialty, f]));
  const specialties = getSpecialtyCatalog();

  const enabledCount = rollout.flags.filter((f) => f.enabled).length;
  const progress =
    rollout.total > 0 ? Math.round((enabledCount / rollout.total) * 100) : 0;

  return (
    <div className="space-y-6 p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Déploiement par spécialité
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Une spécialité, un établissement, à la fois. Le retour arrière est
            immédiat : retirer un établissement de la liste suffit.
          </p>
        </div>
        <Badge variant="outline" className="text-[10px]">
          catalogue {NGAP_CATALOG_HASH.slice(0, 12)}
        </Badge>
      </header>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Spaces déclarés</CardTitle>
            <CardDescription>Workspaces du registre</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold tabular-nums">{specialties.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Activés ici</CardTitle>
            <CardDescription>Pour votre établissement</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold tabular-nums">
              {rollout.loading ? "…" : enabledCount}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Couverture</CardTitle>
            <CardDescription>Sur les {rollout.total || specialties.length} espaces</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Progress value={progress} />
            <p className="text-sm text-muted-foreground tabular-nums">{progress} %</p>
          </CardContent>
        </Card>
      </div>

      {rollout.degraded && (
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <TriangleAlert className="h-4 w-4 text-destructive" />
              Service d'indicateurs indisponible
            </CardTitle>
            <CardDescription>
              L'état du déploiement est inconnu. Les interfaces restent celles
              d'origine : aucune spécialité n'est activée par défaut.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">État par spécialité</CardTitle>
          <CardDescription>
            Le catalogue NGAP indique le nombre d'actes propres à chaque
            spécialité ; les espaces sans acte propre sont cotés dans un
            autre périmètre.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Spécialité</TableHead>
                <TableHead>Actes NGAP</TableHead>
                <TableHead>Indicateur</TableHead>
                <TableHead>État</TableHead>
                <TableHead className="text-right">Espace</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {specialties.map((specialty) => {
                const flag = bySlug.get(specialty.id);
                const enabled = flag?.enabled === true;
                return (
                  <TableRow key={specialty.id}>
                    <TableCell>
                      <div className="font-medium">{specialty.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {specialty.featureFlag}
                      </div>
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {specialty.ngap.catalogueActs}
                    </TableCell>
                    <TableCell>
                      <code className="font-mono text-[10px] text-muted-foreground">
                        {specialty.featureFlag}
                      </code>
                    </TableCell>
                    <TableCell>
                      {enabled ? (
                        <Badge className="gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Activé
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="gap-1">
                          <CircleSlash className="h-3 w-3" />
                          Non activé
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={asRoutePath(ROUTES.specialty)} params={{ specialty: specialty.id } as never}>
                          Ouvrir
                          <ArrowRight className="ml-2 h-3.5 w-3.5" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-4 w-4 text-primary" />
            Comment le retour arrière fonctionne
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            Chaque spécialité a son propre indicateur booléen, activé par une
            liste d'établissements. Le retour arrière consiste à retirer un
            établissement de la liste
            <code className="mx-1 font-mono text-xs">UX_V2_ROLLOUT</code>
            : la prochaine requête revient à l'interface d'origine, sans
            redéploiement et sans perte de données.
          </p>
          <p>
            Une configuration absente ou invalide désactive le déploiement
            : en cas de panne du service d'indicateurs, aucun espace
            expérimental n'est activé.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/** The specialty catalogue, in clinical order. */
function getSpecialtyCatalog() {
  return ALL_SPECIALTIES.map((s) => getSpecialty(s.id)!);
}
